import { ReactNode, useContext } from 'react';
import Image from '@next/image';
import classNames from 'classnames';

import { ApiNumber } from 'types/models/ApiNumber';
import { Brand } from 'types/models/Brand';
import { ConfigContext, ShopKey, getShopKey } from '@providers/ConfigProvider';
import Typography from '@components/atoms/Typography';
import MessageWithBold from '@components/atoms/MessageWithBold';
import Logo from '@templates/Logo';
import { FormattedMessage } from 'utils/intl';
import { getNumbers } from 'utils/getNumber';

import messages from './OurBrands.messages';
import classes from './OurBrands.module.scss';

type Props = {
  className?: string;
  numbers: ApiNumber[];
  brands: Brand[];
};

type NumbersData = Record<'biznesoweStartYear' | 'coconStartYear' | 'dellowoStartYear' | 'premiumStartYear', string>;

const getMessagesMap = ({
  biznesoweStartYear,
  coconStartYear,
  dellowoStartYear,
  premiumStartYear,
}: NumbersData): Record<ShopKey, { description: ReactNode }> => ({
  biznesowe: {
    description: (
      <MessageWithBold
        message={messages.biznesowe}
        values={{
          bold: <FormattedMessage {...messages.bold} values={{ year: biznesoweStartYear }} />,
        }}
      />
    ),
  },
  dellowo: {
    description: (
      <MessageWithBold
        message={messages.dellowo}
        values={{ bold: <FormattedMessage {...messages.bold} values={{ year: dellowoStartYear }} /> }}
      />
    ),
  },
  premiumStation: {
    description: (
      <MessageWithBold
        message={messages.premiumStation}
        values={{
          bold: <FormattedMessage {...messages.bigBold} values={{ year: premiumStartYear }} />,
        }}
      />
    ),
  },
  premiumSerwer: {
    description: <FormattedMessage {...messages.premiumServer} />,
  },
  cocon: {
    description: (
      <MessageWithBold
        message={messages.cocon}
        values={{ bold: <FormattedMessage {...messages.bold} values={{ year: coconStartYear }} /> }}
      />
    ),
  },
  firewall: {
    description: <FormattedMessage {...messages.firewall} />,
  },
  rugged: {
    description: '',
  },
  redarc: {
    description: '',
  },
});

const OurBrands = ({ className, numbers, brands }: Props): JSX.Element => {
  const { shopId, contact } = useContext(ConfigContext);
  const [biznesoweStartYear, coconStartYear, dellowoStartYear, premiumStartYear] = getNumbers(numbers, [
    'BIZNESOWE_START_YEAR',
    'COCON_START_YEAR',
    'DELLOWO_START_YEAR',
    'PREMIUM_START_YEAR',
  ]);

  const shopsMessages = getMessagesMap({
    biznesoweStartYear,
    coconStartYear,
    dellowoStartYear,
    premiumStartYear,
  });

  const shopMessages = shopsMessages[getShopKey(shopId)];

  return (
    <div className={classNames(className, classes.wrapper)}>
      <div className={classes.shop}>
        <Logo height={60} />
        <Typography variant="h1" renderAs="div" className={classNames(classes.header, classes.descriptionAlign)}>
          <FormattedMessage {...messages.here} values={{ shop: contact.appName }} />
        </Typography>
        <Typography variant="h2" renderAs="div" className={classes.descriptionAlign}>
          {shopMessages?.description}
        </Typography>
      </div>
      {brands.map((brand) => {
        const logo = brand.targetShop.files?.find(({ name: fileName }) => fileName === 'logo');

        return (
          <div key={brand.id} className={classes.brandItem}>
            <Image
              display="inline-block"
              alt={`${logo?.name}`}
              resolveConfig={{
                preset: 'svg',
                description: 'logo',
                extension: logo?.file?.extension,
                name: logo?.file?.basename,
                width: logo?.file?.width,
                height: logo?.file?.height,
              }}
            />
            <Typography variant="body1">{shopsMessages[getShopKey(`${brand.targetShop.id}`)].description}</Typography>
          </div>
        );
      })}
    </div>
  );
};

export default OurBrands;
